import React from 'react';
import BackButton from './BackButton';

interface PageProps {
  onBack: () => void;
}

export const PrivacyPage: React.FC<PageProps> = ({ onBack }) => (
  <div className="max-w-4xl mx-auto px-6 py-12 text-slate-700 dark:text-slate-300">
    <BackButton onClick={onBack} />
    <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">Privacy Policy</h1>
    <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">
      Last updated: 27th December 2025
    </p>

    <div className="prose dark:prose-invert max-w-none space-y-6">
      <p className="text-base leading-relaxed">
        At TF Override Generator (accessible from <strong>tfoverrides.dev</strong>), we are
        committed to protecting your privacy and being transparent about how we handle data. This
        Privacy Policy explains what information we collect, how we use it, and your rights
        regarding your data.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        1. Information We Collect
      </h2>

      <h3 className="text-xl font-semibold mt-6 text-slate-900 dark:text-white">
        1.1 Data You Provide
      </h3>
      <p className="text-base leading-relaxed">
        When you use TF Override Generator, you may provide:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>
          <strong>Terraform Module URLs:</strong> GitHub repository URLs that you want to analyze
        </li>
        <li>
          <strong>Variable Definitions:</strong> Names and values of Terraform variables you want to
          override
        </li>
        <li>
          <strong>Cloud Provider Selections:</strong> Your choice of AWS, Azure, GCP, or OCI
        </li>
        <li>
          <strong>Tool Preferences:</strong> Whether you're using Terraform, Terragrunt, or OpenTofu
        </li>
        <li>
          <strong>Feedback Data:</strong> Optional feedback messages you submit through our feedback
          form
        </li>
      </ul>
      <p className="text-base leading-relaxed mt-4">
        <strong>Important:</strong> Your Terraform code and configuration data are processed{' '}
        <strong>locally in your browser</strong>
        whenever possible. We do not store your Terraform configurations, override files, or
        variable values on our servers persistently.
      </p>

      <h3 className="text-xl font-semibold mt-6 text-slate-900 dark:text-white">
        1.2 Automatically Collected Data
      </h3>
      <p className="text-base leading-relaxed">
        Like most websites, we automatically collect certain information:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>
          <strong>Local Storage:</strong> Theme preference (Dark/Light mode) stored on your device
        </li>
        <li>
          <strong>Cookies:</strong> Used by third-party services like Google AdSense (see Section 4)
        </li>
        <li>
          <strong>Usage Analytics:</strong> Anonymous usage statistics to improve the tool (page
          views, feature usage)
        </li>
        <li>
          <strong>Technical Data:</strong> Browser type, device type, operating system, and IP
          address
        </li>
      </ul>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        2. How We Use Your Information
      </h2>
      <p className="text-base leading-relaxed">
        We use the collected information for the following purposes:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>
          <strong>Service Delivery:</strong> To generate Terraform override files based on your
          inputs
        </li>
        <li>
          <strong>AI Processing:</strong> To send your module URLs and variables to Google's Gemini
          API for intelligent code generation
        </li>
        <li>
          <strong>Feature Improvement:</strong> To analyze usage patterns and improve the tool's
          functionality
        </li>
        <li>
          <strong>Bug Fixing:</strong> To diagnose and resolve technical issues
        </li>
        <li>
          <strong>Communication:</strong> To respond to your feedback and support requests
        </li>
        <li>
          <strong>Legal Compliance:</strong> To comply with applicable laws and regulations
        </li>
      </ul>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        3. AI Processing & Data Sharing
      </h2>

      <h3 className="text-xl font-semibold mt-6 text-slate-900 dark:text-white">
        3.1 Google Gemini API
      </h3>
      <p className="text-base leading-relaxed">
        To provide intelligent file generation, specific inputs (such as module URLs, variable
        names, and cloud provider selections) are processed via <strong>Google's Gemini API</strong>
        . This processing happens when you click the "Generate Files" button or use validation
        features.
      </p>
      <p className="text-base leading-relaxed mt-4">
        <strong>Data Retention by Google:</strong> According to Google's API Terms, data sent to
        Gemini API is used solely for generating responses and is{' '}
        <strong>not used to train or improve Google's models</strong>. Google may retain data
        temporarily for abuse prevention and service improvement but does not use customer data for
        advertising purposes.
      </p>

      <h3 className="text-xl font-semibold mt-6 text-slate-900 dark:text-white">3.2 GitHub API</h3>
      <p className="text-base leading-relaxed">
        When you provide a GitHub module URL, we use the <strong>GitHub API</strong> to fetch the
        repository's variables.tf file. This request is made directly from our server to GitHub and
        does not involve storing your code.
      </p>

      <h3 className="text-xl font-semibold mt-6 text-slate-900 dark:text-white">
        3.3 Local Processing Fallback
      </h3>
      <p className="text-base leading-relaxed">
        Our tool includes a <strong>local parsing service</strong> that can analyze and validate
        Terraform code entirely within your browser without sending data to external APIs. This
        fallback is used automatically when GitHub or AI APIs are unavailable.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        4. Third-Party Services & Advertising
      </h2>

      <h3 className="text-xl font-semibold mt-6 text-slate-900 dark:text-white">
        4.1 Google AdSense
      </h3>
      <p className="text-base leading-relaxed">
        We use <strong>Google AdSense</strong> to serve advertisements on our website. Google may
        use cookies (including the DART cookie) to serve ads based on your visit to this site and
        other sites on the internet.
      </p>
      <p className="text-base leading-relaxed mt-4">
        <strong>How to Opt-Out:</strong> You can opt out of personalized advertising by visiting
        <a
          href="https://www.google.com/settings/ads"
          target="_blank"
          rel="noopener noreferrer"
          className="text-indigo-600 dark:text-indigo-400 hover:underline ml-1"
        >
          Google's Ads Settings
        </a>{' '}
        or by using browser extensions that block advertising cookies.
      </p>

      <h3 className="text-xl font-semibold mt-6 text-slate-900 dark:text-white">
        4.2 Analytics Services
      </h3>
      <p className="text-base leading-relaxed">
        We use analytics services to understand how users interact with our tool:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
        <li>
          <strong>Google Analytics 4:</strong> We use Google Analytics to collect anonymous usage
          data such as page views, session duration, feature usage, and geographic location. This
          helps us understand which features are most valuable and improve the user experience.
          Google Analytics uses cookies and similar tracking technologies. You can opt out by using
          browser extensions or visiting{' '}
          <a
            href="https://tools.google.com/dlpage/gaoptout"
            target="_blank"
            rel="noopener noreferrer"
            className="text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Google Analytics Opt-out Browser Add-on
          </a>
          .
        </li>
        <li>
          <strong>Cloudflare Web Analytics:</strong> We use Cloudflare's privacy-first analytics
          which does not use cookies or track individual users. It provides basic traffic metrics
          while respecting your privacy.
        </li>
      </ul>
      <p className="text-base leading-relaxed mt-4">
        These services collect anonymous usage data to help us improve the tool and understand user
        needs.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">5. Data Security</h2>
      <p className="text-base leading-relaxed">
        We implement industry-standard security measures to protect your data:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>
          <strong>HTTPS Encryption:</strong> All data transmitted between your browser and our
          servers is encrypted using TLS/SSL
        </li>
        <li>
          <strong>No Persistent Storage:</strong> Generated Terraform files are not stored on our
          servers
        </li>
        <li>
          <strong>API Proxy:</strong> Our server acts as a proxy to protect API keys and rate limits
        </li>
        <li>
          <strong>Local-First Processing:</strong> Most operations happen in your browser without
          server involvement
        </li>
      </ul>
      <p className="text-base leading-relaxed mt-4">
        However, no method of transmission over the internet is 100% secure. While we strive to
        protect your data, we cannot guarantee absolute security.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        6. Your Rights & Choices
      </h2>
      <p className="text-base leading-relaxed">
        Depending on your location, you may have the following rights:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>
          <strong>Access:</strong> Request a copy of the data we hold about you
        </li>
        <li>
          <strong>Correction:</strong> Request correction of inaccurate data
        </li>
        <li>
          <strong>Deletion:</strong> Request deletion of your data (where applicable)
        </li>
        <li>
          <strong>Opt-Out:</strong> Disable cookies and personalized advertising
        </li>
        <li>
          <strong>Portability:</strong> Request your data in a machine-readable format
        </li>
      </ul>
      <p className="text-base leading-relaxed mt-4">
        To exercise these rights, contact us at{' '}
        <a
          href="mailto:help@tfoverrides.dev"
          className="text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          help@tfoverrides.dev
        </a>
        .
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        7. Children's Privacy
      </h2>
      <p className="text-base leading-relaxed">
        TF Override Generator is intended for professional use by software engineers and DevOps
        practitioners. We do not knowingly collect personal information from children under 13 years
        of age. If you believe we have inadvertently collected data from a child, please contact us
        immediately.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        8. International Data Transfers
      </h2>
      <p className="text-base leading-relaxed">
        Our services are hosted on <strong>Google Cloud Platform</strong>, which may process data in
        multiple regions globally. By using our service, you consent to your data being transferred
        to and processed in countries outside your residence, which may have different data
        protection laws.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        9. Changes to This Policy
      </h2>
      <p className="text-base leading-relaxed">
        We may update this Privacy Policy from time to time to reflect changes in our practices or
        legal requirements. We will notify users of significant changes by updating the "Last
        updated" date at the top of this page. Continued use of the service after changes
        constitutes acceptance of the updated policy.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">10. Contact Us</h2>
      <p className="text-base leading-relaxed">
        If you have questions, concerns, or requests regarding this Privacy Policy or our data
        practices, please contact us:
      </p>
      <div className="mt-4 p-6 bg-indigo-50 dark:bg-slate-800 rounded-lg border border-indigo-200 dark:border-slate-700">
        <p className="font-semibold text-slate-900 dark:text-white mb-2">TF Override Generator</p>
        <p className="text-sm text-slate-700 dark:text-slate-300">
          Email:{' '}
          <a
            href="mailto:help@tfoverrides.dev"
            className="text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            help@tfoverrides.dev
          </a>
        </p>
        <p className="text-sm text-slate-700 dark:text-slate-300 mt-2">
          Or use the Feedback button within the application for quick inquiries.
        </p>
      </div>

      <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-slate-700 dark:text-slate-300">
          <strong>Summary:</strong> We prioritize your privacy. Your Terraform code stays local when
          possible, we don't sell your data, and we're transparent about how AI processing works.
          Contact us anytime with questions.
        </p>
      </div>
    </div>
  </div>
);

export const TermsPage: React.FC<PageProps> = ({ onBack }) => (
  <div className="max-w-4xl mx-auto px-6 py-12 text-slate-700 dark:text-slate-300">
    <BackButton onClick={onBack} />
    <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-2">Terms of Service</h1>
    <p className="text-sm text-slate-500 dark:text-slate-400 mb-8">
      Last updated: 27th December 2025
    </p>

    <div className="prose dark:prose-invert max-w-none space-y-6">
      <p className="text-base leading-relaxed">
        Welcome to TF Override Generator (accessible from <strong>tfoverrides.dev</strong>). By
        accessing or using this website and its services, you agree to be bound by these Terms of
        Service. If you do not agree to these terms, please do not use our service.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        1. Acceptance of Terms
      </h2>
      <p className="text-base leading-relaxed">
        By accessing this website, you agree to be bound by these Terms and Conditions of Use and
        agree that you are responsible for compliance with any applicable local laws. These terms
        apply to all visitors, users, and others who access or use the service.
      </p>
      <p className="text-base leading-relaxed mt-4">
        We reserve the right to modify these terms at any time. Changes will be effective
        immediately upon posting. Your continued use of the service after changes are posted
        constitutes acceptance of the modified terms.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        2. Description of Service
      </h2>
      <p className="text-base leading-relaxed">
        TF Override Generator is a <strong>free, AI-powered tool</strong> designed to help DevOps
        engineers and infrastructure practitioners generate Terraform, Terragrunt, and OpenTofu
        override files. The service includes:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4 text-slate-700 dark:text-slate-300">
        <li>Automated parsing of Terraform module variables from GitHub repositories</li>
        <li>AI-powered generation of override files (override.tf, provider.tf)</li>
        <li>Support for Terragrunt input blocks</li>
        <li>HCL syntax validation and formatting</li>
        <li>Educational guides and documentation</li>
      </ul>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">3. Use License</h2>

      <h3 className="text-xl font-semibold mt-6 text-slate-900 dark:text-white">
        3.1 Permitted Use
      </h3>
      <p className="text-base leading-relaxed">Permission is granted to:</p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>Download and use the generated Terraform code for personal or commercial purposes</li>
        <li>Modify, adapt, and integrate the generated code into your infrastructure</li>
        <li>Share the generated code within your organization or team</li>
        <li>Use the tool for educational and learning purposes</li>
      </ul>
      <p className="text-base leading-relaxed mt-4">
        This is the grant of a license, not a transfer of title. You own the code you generate, but
        the service itself remains the property of TF Override Generator.
      </p>

      <h3 className="text-xl font-semibold mt-6 text-slate-900 dark:text-white">
        3.2 Prohibited Use
      </h3>
      <p className="text-base leading-relaxed">You agree NOT to:</p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>Use the service for any illegal or unauthorized purpose</li>
        <li>Attempt to reverse engineer, decompile, or disassemble the service</li>
        <li>Use automated scripts or bots to abuse the service or bypass rate limits</li>
        <li>Resell, redistribute, or create derivative services based on TF Override Generator</li>
        <li>Intentionally introduce viruses, malware, or malicious code</li>
        <li>Violate any applicable laws or regulations</li>
        <li>Infringe on intellectual property rights of others</li>
      </ul>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        4. Disclaimer of Warranties
      </h2>
      <p className="text-base leading-relaxed">
        <strong>IMPORTANT - READ CAREFULLY:</strong>
      </p>
      <div className="mt-4 p-6 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
        <p className="text-base leading-relaxed font-semibold text-slate-900 dark:text-white mb-2">
          ⚠️ Generated Code is Provided "AS IS"
        </p>
        <p className="text-base leading-relaxed">
          The materials on TF Override Generator, including all generated Terraform, Terragrunt, and
          OpenTofu code, are provided
          <strong> "AS IS" WITHOUT WARRANTIES OF ANY KIND</strong>, either express or implied.
        </p>
        <p className="text-base leading-relaxed mt-4">
          We make <strong>NO WARRANTIES</strong> regarding:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
          <li>The accuracy, reliability, or correctness of the generated code</li>
          <li>The suitability of the code for your specific use case</li>
          <li>The security or compliance of the generated configurations</li>
          <li>The availability or uptime of the service</li>
          <li>The compatibility with your infrastructure or cloud providers</li>
        </ul>
      </div>
      <p className="text-base leading-relaxed mt-6">
        <strong>YOU ARE SOLELY RESPONSIBLE</strong> for reviewing, testing, and validating all
        generated code before deploying it to any environment, especially production. Always have a
        qualified engineer or infrastructure specialist review the code.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        5. Limitation of Liability
      </h2>
      <p className="text-base leading-relaxed">
        <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
      </p>
      <p className="text-base leading-relaxed mt-4">
        In no event shall TF Override Generator, its operators, contributors, or suppliers be liable
        for any damages (including, without limitation, damages for):
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>
          <strong>Infrastructure Failures:</strong> Downtime, data loss, or service interruptions
          caused by generated code
        </li>
        <li>
          <strong>Financial Loss:</strong> Cloud provider costs, lost revenue, or business
          interruption
        </li>
        <li>
          <strong>Security Incidents:</strong> Data breaches, unauthorized access, or compliance
          violations
        </li>
        <li>
          <strong>Data Loss:</strong> Corruption or loss of data resulting from using generated code
        </li>
        <li>
          <strong>Service Unavailability:</strong> Inability to access the tool or generate code
        </li>
        <li>
          <strong>Third-Party Issues:</strong> Problems with GitHub, Google Cloud, or AI services
        </li>
      </ul>
      <p className="text-base leading-relaxed mt-4">
        This limitation applies whether the liability arises from contract, tort, negligence, strict
        liability, or any other legal theory, even if we have been advised of the possibility of
        such damages.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">6. Indemnification</h2>
      <p className="text-base leading-relaxed">
        You agree to <strong>indemnify, defend, and hold harmless</strong> TF Override Generator and
        its operators from any claims, damages, losses, liabilities, and expenses (including legal
        fees) arising from:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>Your use of the service or generated code</li>
        <li>Your violation of these Terms of Service</li>
        <li>Your violation of any rights of another party</li>
        <li>Your deployment of generated code to production environments</li>
      </ul>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        7. User Responsibilities
      </h2>
      <p className="text-base leading-relaxed">As a user of this service, you agree to:</p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>
          <strong>Review All Generated Code:</strong> Manually inspect all generated files before
          use
        </li>
        <li>
          <strong>Test in Non-Production Environments:</strong> Validate code in dev/staging before
          production
        </li>
        <li>
          <strong>Follow Best Practices:</strong> Adhere to Terraform and cloud provider best
          practices
        </li>
        <li>
          <strong>Secure Your Infrastructure:</strong> Implement proper access controls and security
          measures
        </li>
        <li>
          <strong>Maintain Backups:</strong> Keep backups of your infrastructure state and
          configurations
        </li>
        <li>
          <strong>Respect Rate Limits:</strong> Do not abuse the service or send excessive requests
        </li>
      </ul>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        8. Third-Party Services
      </h2>
      <p className="text-base leading-relaxed">
        Our service integrates with third-party platforms:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>
          <strong>Google Gemini API:</strong> For AI-powered code generation
        </li>
        <li>
          <strong>GitHub API:</strong> For fetching module variables
        </li>
        <li>
          <strong>Google AdSense:</strong> For displaying advertisements
        </li>
        <li>
          <strong>Google Cloud Platform:</strong> For hosting and infrastructure
        </li>
      </ul>
      <p className="text-base leading-relaxed mt-4">
        Your use of these third-party services is subject to their respective terms of service and
        privacy policies. We are not responsible for the actions, content, or policies of these
        third parties.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        9. Intellectual Property
      </h2>

      <h3 className="text-xl font-semibold mt-6 text-slate-900 dark:text-white">
        9.1 Your Generated Code
      </h3>
      <p className="text-base leading-relaxed">
        You retain all rights to the Terraform code you generate using our tool. We do not claim
        ownership of your generated configurations.
      </p>

      <h3 className="text-xl font-semibold mt-6 text-slate-900 dark:text-white">9.2 Our Service</h3>
      <p className="text-base leading-relaxed">
        The TF Override Generator service, including its design, code, algorithms, user interface,
        and documentation, is the property of TF Override Generator and is protected by copyright,
        trademark, and other intellectual property laws.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        10. Service Availability
      </h2>
      <p className="text-base leading-relaxed">
        We strive to maintain high availability but do not guarantee:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>Uninterrupted or error-free operation</li>
        <li>Availability of AI or GitHub APIs</li>
        <li>Specific uptime percentages or SLAs</li>
        <li>Permanent availability of the service</li>
      </ul>
      <p className="text-base leading-relaxed mt-4">
        We reserve the right to modify, suspend, or discontinue the service (or any part thereof) at
        any time without notice.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">11. Privacy</h2>
      <p className="text-base leading-relaxed">
        Your use of the service is also governed by our{' '}
        <a href="/privacy" className="text-indigo-600 dark:text-indigo-400 hover:underline">
          Privacy Policy
        </a>
        , which explains how we collect, use, and protect your data.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">12. Termination</h2>
      <p className="text-base leading-relaxed">
        We may terminate or suspend your access to the service immediately, without prior notice or
        liability, for any reason, including:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>Violation of these Terms of Service</li>
        <li>Abusive or fraudulent use of the service</li>
        <li>At our sole discretion</li>
      </ul>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">13. Governing Law</h2>
      <p className="text-base leading-relaxed">
        These Terms shall be governed by and construed in accordance with the laws of the
        jurisdiction where the service operator resides, without regard to its conflict of law
        provisions.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">14. Severability</h2>
      <p className="text-base leading-relaxed">
        If any provision of these Terms is found to be unenforceable or invalid, that provision will
        be limited or eliminated to the minimum extent necessary, and the remaining provisions will
        remain in full force and effect.
      </p>

      <h2 className="text-2xl font-bold mt-8 text-slate-900 dark:text-white">
        15. Contact Information
      </h2>
      <p className="text-base leading-relaxed">
        For questions about these Terms of Service, please contact us:
      </p>
      <div className="mt-4 p-6 bg-indigo-50 dark:bg-slate-800 rounded-lg border border-indigo-200 dark:border-slate-700">
        <p className="font-semibold text-slate-900 dark:text-white mb-2">TF Override Generator</p>
        <p className="text-sm text-slate-700 dark:text-slate-300">
          Email:{' '}
          <a
            href="mailto:help@tfoverrides.dev"
            className="text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            help@tfoverrides.dev
          </a>
        </p>
      </div>

      <div className="mt-8 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
        <p className="text-sm text-slate-700 dark:text-slate-300 font-semibold mb-2">
          🚨 Critical Reminder:
        </p>
        <p className="text-sm text-slate-700 dark:text-slate-300">
          Generated Terraform code should <strong>ALWAYS</strong> be reviewed by a qualified
          engineer before deployment to production. We are not responsible for infrastructure
          failures, data loss, or security incidents resulting from the use of generated code. Test
          thoroughly in non-production environments first.
        </p>
      </div>

      <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-8 pt-8 border-t border-slate-200 dark:border-slate-700">
        By using TF Override Generator, you acknowledge that you have read, understood, and agree to
        be bound by these Terms of Service.
      </p>
    </div>
  </div>
);
